/**
 * Entity Management Page
 * Manages corporate structure with parent/subsidiary hierarchy.
 * 
 * Features:
 * - Entity CRUD operations
 * - Org chart visualization
 * - Auto-detected regulations
 * - Data migration tool
 */

import React, { useState, useEffect } from 'react';
import { 
  Building2, Plus, ChevronRight, ChevronDown, Globe, Users, 
  DollarSign, Scale, Pencil, Trash2, Eye, AlertTriangle,
  CheckCircle, Clock, ArrowRight, RefreshCw, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../../services/api_service';
import { JourneySection, JourneyPanel } from '../journeys/JourneyPanel';
import { RegulatoryShockPanel, PeerBenchmarkPanel, GroupHierarchyPanel } from '../journeys/JourneyDashboards';

// Country options for jurisdiction
const COUNTRIES = [
  { value: 'USA', label: 'United States', region: 'Americas' },
  { value: 'Germany', label: 'Germany', region: 'Europe' },
  { value: 'France', label: 'France', region: 'Europe' },
  { value: 'UK', label: 'United Kingdom', region: 'Europe' },
  { value: 'Netherlands', label: 'Netherlands', region: 'Europe' },
  { value: 'Spain', label: 'Spain', region: 'Europe' },
  { value: 'Italy', label: 'Italy', region: 'Europe' },
  { value: 'Poland', label: 'Poland', region: 'Europe' },
  { value: 'Belgium', label: 'Belgium', region: 'Europe' },
  { value: 'Singapore', label: 'Singapore', region: 'APAC' },
  { value: 'China', label: 'China', region: 'APAC' },
  { value: 'Japan', label: 'Japan', region: 'APAC' },
  { value: 'Australia', label: 'Australia', region: 'APAC' },
  { value: 'India', label: 'India', region: 'APAC' },
  { value: 'Canada', label: 'Canada', region: 'Americas' },
  { value: 'Mexico', label: 'Mexico', region: 'Americas' },
  { value: 'Brazil', label: 'Brazil', region: 'Americas' },
];

const CURRENCIES = [
  { value: 'USD', label: 'USD ($)', symbol: '$' },
  { value: 'EUR', label: 'EUR (€)', symbol: '€' },
  { value: 'GBP', label: 'GBP (£)', symbol: '£' },
  { value: 'SGD', label: 'SGD (S$)', symbol: 'S$' },
  { value: 'CNY', label: 'CNY (¥)', symbol: '¥' },
  { value: 'JPY', label: 'JPY (¥)', symbol: '¥' },
];

export default function EntityManagementPage() {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'hierarchy'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [editingEntity, setEditingEntity] = useState(null);

  useEffect(() => {
    fetchEntities();
  }, []);

  const fetchEntities = async () => {
    try {
      setLoading(true);
      const data = await apiFetch('/entities');
      if (!data?._error) {
        setEntities(data.entities || []);
      } else {
        console.error('Error fetching entities:', data._error);
        toast.error('Failed to load entities');
      }
    } catch (error) {
      console.error('Error fetching entities:', error);
      toast.error('Failed to load entities');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEntity = async (entityData) => {
    try {
      const newEntity = await apiFetch('/entities', {
        method: 'POST',
        body: entityData
      });
      
      if (newEntity?._error) throw new Error('Failed to create entity');
      
      setEntities([...entities, newEntity]);
      setShowCreateModal(false);
      toast.success(`Entity "${entityData.legalName}" created successfully`);
    } catch (error) {
      console.error('Error creating entity:', error);
      toast.error('Failed to create entity');
    }
  };

  const handleUpdateEntity = async (entityId, updateData) => {
    try {
      const updatedEntity = await apiFetch(`/entities/${entityId}`, {
        method: 'PUT',
        body: updateData
      });
      
      if (updatedEntity?._error) throw new Error('Failed to update entity');
      
      setEntities(entities.map(e => e._id === entityId ? updatedEntity : e));
      setEditingEntity(null);
      toast.success('Entity updated successfully');
    } catch (error) {
      console.error('Error updating entity:', error);
      toast.error('Failed to update entity');
    }
  };

  const handleDeleteEntity = async (entityId) => {
    if (!window.confirm('Are you sure you want to delete this entity?')) return;
    
    try {
      const result = await apiFetch(`/entities/${entityId}`, {
        method: 'DELETE'
      });
      
      if (result?._error) throw new Error('Failed to delete entity');
      
      setEntities(entities.filter(e => e._id !== entityId));
      toast.success('Entity deleted successfully');
    } catch (error) {
      console.error('Error deleting entity:', error);
      toast.error('Failed to delete entity');
    }
  };

  const handleMigrateData = async (defaultEntityId) => {
    try {
      const result = await apiFetch('/entities/migrate-existing-data', {
        method: 'POST',
        body: { defaultEntityId }
      });
      
      if (result?._error) throw new Error('Failed to migrate data');
      
      setShowMigrationModal(false);
      toast.success(`Migrated: ${result.stats.obligationsMigrated} obligations, ${result.stats.evidenceMigrated} evidence items`);
    } catch (error) {
      console.error('Error migrating data:', error);
      toast.error('Failed to migrate data');
    }
  };

  // Calculate stats
  const stats = {
    total: entities.length,
    parents: entities.filter(e => e.entityType === 'parent').length,
    subsidiaries: entities.filter(e => e.entityType === 'subsidiary').length,
    countries: new Set(entities.map(e => e.jurisdiction?.country)).size,
    regulations: new Set(entities.flatMap(e => (e.applicableRegulations || []).map(r => r.regulationCode))).size
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="entity-management-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 px-8 py-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="h-8 w-8 text-blue-300" />
              <h1 className="text-3xl font-bold text-white">Entity Management</h1>
            </div>
            <p className="text-blue-200">Manage your corporate structure and multi-entity compliance</p>
          </div>
          <div className="flex gap-3">
            {entities.length > 0 && (
              <button
                onClick={() => setShowMigrationModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-colors"
                data-testid="migrate-data-btn"
              >
                <Upload className="h-4 w-4" />
                Migrate Existing Data
              </button>
            )}
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 rounded-lg text-white hover:bg-purple-700 transition-colors"
              data-testid="add-entity-btn"
            >
              <Plus className="h-4 w-4" />
              Add Entity
            </button>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="px-8 py-6">
        <div className="grid grid-cols-5 gap-4">
          <StatCard icon={Building2} label="Total Entities" value={stats.total} />
          <StatCard icon={Building2} label="Parent Entities" value={stats.parents} color="purple" />
          <StatCard icon={Building2} label="Subsidiaries" value={stats.subsidiaries} color="blue" />
          <StatCard icon={Globe} label="Countries" value={stats.countries} color="green" />
          <StatCard icon={Scale} label="Regulations" value={stats.regulations} color="orange" />
        </div>
      </div>

      {/* View Toggle */}
      <div className="px-8 mb-4">
        <div className="flex gap-2 bg-[#1a1a1a] p-1 rounded-lg w-fit">
          <button
            onClick={() => setViewMode('table')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'table' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            Table View
          </button>
          <button
            onClick={() => setViewMode('hierarchy')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'hierarchy' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            Org Chart
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-8 pb-8">
        {entities.length === 0 ? (
          <EmptyState onCreateClick={() => setShowCreateModal(true)} />
        ) : viewMode === 'table' ? (
          <EntityTable 
            entities={entities} 
            onEdit={setEditingEntity}
            onDelete={handleDeleteEntity}
            onView={setSelectedEntity}
          />
        ) : (
          <EntityHierarchy entities={entities} onEdit={setEditingEntity} />
        )}
      </div>

      {/* Create Entity Modal */}
      {showCreateModal && (
        <CreateEntityModal
          parentEntities={entities.filter(e => e.entityType === 'parent')}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateEntity}
        />
      )}

      {/* Edit Entity Modal */}
      {editingEntity && (
        <EditEntityModal
          entity={editingEntity}
          onClose={() => setEditingEntity(null)}
          onSubmit={(data) => handleUpdateEntity(editingEntity._id, data)}
        />
      )}

      {/* View Entity Modal */}
      {selectedEntity && (
        <ViewEntityModal
          entity={selectedEntity}
          onClose={() => setSelectedEntity(null)}
        />
      )}

      {/* Migration Modal */}
      {showMigrationModal && (
        <MigrationModal
          entities={entities}
          onClose={() => setShowMigrationModal(false)}
          onMigrate={handleMigrateData}
        />
      )}

      {/* Live User Journeys */}
      <div className="px-8 pb-8">
        <JourneySection title="Live Journeys">
          <JourneyPanel title="M&A Regulatory Shock Simulator" ucId="UC-15"><RegulatoryShockPanel /></JourneyPanel>
          <JourneyPanel title="Multi-Entity Group Hierarchy" ucId="UC-02"><GroupHierarchyPanel /></JourneyPanel>
          <JourneyPanel title="Peer Benchmarking" ucId="UC-23"><PeerBenchmarkPanel /></JourneyPanel>
        </JourneySection>
      </div>
    </div>
  );
}

// Stat Card Component
function StatCard({ icon: Icon, label, value, color = 'gray' }) {
  const colors = {
    gray: 'from-gray-800 to-gray-900 border-gray-700',
    purple: 'from-purple-900/50 to-purple-950/50 border-purple-700/50',
    blue: 'from-blue-900/50 to-blue-950/50 border-blue-700/50',
    green: 'from-green-900/50 to-green-950/50 border-green-700/50',
    orange: 'from-orange-900/50 to-orange-950/50 border-orange-700/50'
  };

  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-4`}>
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-gray-400" />
        <div>
          <p className="text-2xl font-bold text-white">{value}</p>
          <p className="text-xs text-gray-400">{label}</p>
        </div>
      </div>
    </div>
  );
}

// Empty State Component
function EmptyState({ onCreateClick }) {
  return (
    <div className="bg-[#1a1a1a] border border-gray-800 rounded-xl p-12 text-center">
      <Building2 className="h-16 w-16 text-gray-600 mx-auto mb-4" />
      <h3 className="text-xl font-semibold text-white mb-2">No Entities Yet</h3>
      <p className="text-gray-400 mb-6 max-w-md mx-auto">
        Start by adding your parent entity (holding company), then add subsidiaries to build your corporate structure.
      </p>
      <button
        onClick={onCreateClick}
        className="flex items-center gap-2 px-6 py-3 bg-purple-600 rounded-lg text-white hover:bg-purple-700 mx-auto"
        data-testid="empty-add-entity-btn"
      >
        <Plus className="h-4 w-4" />
        Add Your First Entity
      </button>
    </div>
  );
}

// Entity Table Component
function EntityTable({ entities, onEdit, onDelete, onView }) {
  return (
    <div className="bg-[#1a1a1a] border border-gray-800 rounded-xl overflow-hidden">
      <table className="w-full" data-testid="entities-table">
        <thead>
          <tr className="border-b border-gray-800 bg-gray-900/50">
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Entity</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Type</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Country</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Revenue</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Employees</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Regulations</th>
            <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800">
          {entities.map((entity) => (
            <tr key={entity._id} className="hover:bg-gray-800/50 transition-colors">
              <td className="px-6 py-4">
                <div>
                  <p className="text-white font-medium">{entity.legalName}</p>
                  {entity.tradingName && entity.tradingName !== entity.legalName && (
                    <p className="text-xs text-gray-500">d/b/a {entity.tradingName}</p>
                  )}
                </div>
              </td>
              <td className="px-6 py-4">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  entity.entityType === 'parent' 
                    ? 'bg-purple-500/20 text-purple-300' 
                    : 'bg-blue-500/20 text-blue-300'
                }`}>
                  {entity.entityType}
                </span>
              </td>
              <td className="px-6 py-4 text-gray-300">{entity.jurisdiction?.country || '-'}</td>
              <td className="px-6 py-4 text-gray-300">
                {entity.financial?.revenue2024 
                  ? `${(entity.financial.revenue2024 / 1000000).toFixed(0)}M`
                  : '-'
                }
              </td>
              <td className="px-6 py-4 text-gray-300">
                {entity.financial?.employees2024?.toLocaleString() || '-'}
              </td>
              <td className="px-6 py-4">
                <div className="flex flex-wrap gap-1">
                  {(entity.applicableRegulations || []).slice(0, 3).map((reg, idx) => (
                    <span key={idx} className="px-2 py-0.5 bg-gray-700 rounded text-xs text-gray-300">
                      {reg.regulationCode}
                    </span>
                  ))}
                  {(entity.applicableRegulations || []).length > 3 && (
                    <span className="px-2 py-0.5 bg-gray-700 rounded text-xs text-gray-300">
                      +{entity.applicableRegulations.length - 3}
                    </span>
                  )}
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => onView(entity)}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                    title="View"
                    data-testid={`view-entity-${entity._id}`}
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onEdit(entity)}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                    title="Edit"
                    data-testid={`edit-entity-${entity._id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onDelete(entity._id)}
                    className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg transition-colors"
                    title="Delete"
                    data-testid={`delete-entity-${entity._id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Entity Hierarchy (Org Chart) Component
function EntityHierarchy({ entities, onEdit }) {
  // Build tree structure
  const buildTree = () => {
    const entityMap = {};
    const roots = [];
    
    entities.forEach(e => {
      entityMap[e._id] = { ...e, children: [] };
    });
    
    entities.forEach(e => {
      const parentId = e.hierarchy?.parentEntityId;
      if (parentId && entityMap[parentId]) {
        entityMap[parentId].children.push(entityMap[e._id]);
      } else {
        roots.push(entityMap[e._id]);
      }
    });
    
    return roots;
  };

  const tree = buildTree();

  const renderNode = (node, level = 0) => (
    <div key={node._id} className={`${level > 0 ? 'ml-8 mt-2' : ''}`}>
      <div className={`
        p-4 rounded-xl border-2 transition-all cursor-pointer hover:border-purple-500
        ${node.entityType === 'parent' 
          ? 'bg-purple-900/30 border-purple-700/50' 
          : 'bg-gray-800/50 border-gray-700'
        }
      `}
      onClick={() => onEdit(node)}
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-gray-500 uppercase">{node.entityType}</p>
            <p className="text-white font-semibold">{node.legalName}</p>
            <p className="text-sm text-gray-400">{node.jurisdiction?.country}</p>
          </div>
          <div className="text-right">
            {node.financial?.revenue2024 && (
              <p className="text-sm font-medium text-white">
                {node.financial.reportingCurrency} {(node.financial.revenue2024 / 1000000000).toFixed(1)}B
              </p>
            )}
            {node.financial?.employees2024 && (
              <p className="text-xs text-gray-500">
                {node.financial.employees2024.toLocaleString()} employees
              </p>
            )}
          </div>
        </div>
        {node.applicableRegulations?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {node.applicableRegulations.map((reg, idx) => (
              <span key={idx} className="px-2 py-0.5 bg-gray-700 rounded text-xs text-gray-300">
                {reg.regulationCode}
              </span>
            ))}
          </div>
        )}
      </div>
      {node.children?.length > 0 && (
        <div className="border-l-2 border-gray-700 pl-4 mt-2">
          {node.children.map(child => renderNode(child, level + 1))}
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-[#1a1a1a] border border-gray-800 rounded-xl p-6" data-testid="entity-hierarchy">
      <h3 className="text-lg font-semibold text-white mb-4">Corporate Hierarchy</h3>
      <div className="space-y-4">
        {tree.map(node => renderNode(node))}
      </div>
    </div>
  );
}

// Create Entity Modal
function CreateEntityModal({ parentEntities, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    legalName: '',
    tradingName: '',
    entityType: parentEntities.length === 0 ? 'parent' : 'subsidiary',
    parentEntityId: '',
    jurisdiction: { country: '', region: '' },
    financial: {
      reportingCurrency: 'USD',
      revenue2024: '',
      employees2024: '',
      isPubliclyListed: false
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      level: formData.entityType === 'parent' ? 0 : 1,
      financial: {
        ...formData.financial,
        revenue2024: formData.financial.revenue2024 ? parseInt(formData.financial.revenue2024) : null,
        employees2024: formData.financial.employees2024 ? parseInt(formData.financial.employees2024) : null
      }
    };
    onSubmit(submitData);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="create-entity-modal">
      <div className="bg-[#1a1a1a] border border-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-800">
          <h2 className="text-xl font-bold text-white">Add New Entity</h2>
          <p className="text-sm text-gray-400 mt-1">
            Create a new entity in your corporate structure
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Entity Type */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Entity Type</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="entityType"
                  value="parent"
                  checked={formData.entityType === 'parent'}
                  onChange={(e) => setFormData({ ...formData, entityType: e.target.value, parentEntityId: '' })}
                  className="text-purple-600"
                />
                <span className="text-white">Parent / Holding Company</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="entityType"
                  value="subsidiary"
                  checked={formData.entityType === 'subsidiary'}
                  onChange={(e) => setFormData({ ...formData, entityType: e.target.value })}
                  className="text-purple-600"
                />
                <span className="text-white">Subsidiary</span>
              </label>
            </div>
          </div>

          {/* Parent Entity (if subsidiary) */}
          {formData.entityType === 'subsidiary' && parentEntities.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Parent Entity</label>
              <select
                value={formData.parentEntityId}
                onChange={(e) => setFormData({ ...formData, parentEntityId: e.target.value })}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              >
                <option value="">Select parent entity...</option>
                {parentEntities.map(parent => (
                  <option key={parent._id} value={parent._id}>
                    {parent.legalName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Legal Name */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Legal Entity Name *</label>
            <input
              type="text"
              value={formData.legalName}
              onChange={(e) => setFormData({ ...formData, legalName: e.target.value })}
              placeholder="e.g., Acme Corp Holdings Inc."
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
              data-testid="entity-legal-name"
            />
          </div>

          {/* Trading Name */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Trading Name (if different)</label>
            <input
              type="text"
              value={formData.tradingName}
              onChange={(e) => setFormData({ ...formData, tradingName: e.target.value })}
              placeholder="e.g., Acme Corp"
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Jurisdiction */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Country *</label>
              <select
                value={formData.jurisdiction.country}
                onChange={(e) => {
                  const country = COUNTRIES.find(c => c.value === e.target.value);
                  setFormData({
                    ...formData,
                    jurisdiction: { 
                      country: e.target.value, 
                      region: country?.region || '' 
                    }
                  });
                }}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
                data-testid="entity-country"
              >
                <option value="">Select country...</option>
                {COUNTRIES.map(country => (
                  <option key={country.value} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Currency</label>
              <select
                value={formData.financial.reportingCurrency}
                onChange={(e) => setFormData({
                  ...formData,
                  financial: { ...formData.financial, reportingCurrency: e.target.value }
                })}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {CURRENCIES.map(curr => (
                  <option key={curr.value} value={curr.value}>{curr.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Financial Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Annual Revenue (2024)</label>
              <input
                type="number"
                value={formData.financial.revenue2024}
                onChange={(e) => setFormData({
                  ...formData,
                  financial: { ...formData.financial, revenue2024: e.target.value }
                })}
                placeholder="e.g., 5000000000"
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                data-testid="entity-revenue"
              />
              <p className="text-xs text-gray-500 mt-1">In {formData.financial.reportingCurrency}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Number of Employees</label>
              <input
                type="number"
                value={formData.financial.employees2024}
                onChange={(e) => setFormData({
                  ...formData,
                  financial: { ...formData.financial, employees2024: e.target.value }
                })}
                placeholder="e.g., 10000"
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Publicly Listed */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.financial.isPubliclyListed}
                onChange={(e) => setFormData({
                  ...formData,
                  financial: { ...formData.financial, isPubliclyListed: e.target.checked }
                })}
                className="w-5 h-5 rounded border-gray-600 text-purple-600 focus:ring-purple-500"
              />
              <span className="text-white">This entity is publicly listed</span>
            </label>
          </div>

          {/* Info Box */}
          <div className="bg-blue-900/30 border border-blue-700/50 rounded-lg p-4">
            <p className="text-sm text-blue-200">
              <strong>Why we need this:</strong> Revenue and employee count determine 
              which regulations apply to your entity (e.g., post-Omnibus CSRD applies when both >1,000 employees and >€450M turnover are met).
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-800">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              data-testid="submit-entity-btn"
            >
              Create Entity
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit Entity Modal (simplified - reuses most of CreateEntityModal logic)
function EditEntityModal({ entity, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    legalName: entity.legalName || '',
    tradingName: entity.tradingName || '',
    jurisdiction: entity.jurisdiction || { country: '', region: '' },
    financial: entity.financial || { reportingCurrency: 'USD', revenue2024: '', employees2024: '' }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      legalName: formData.legalName,
      tradingName: formData.tradingName,
      jurisdiction: formData.jurisdiction,
      financial: {
        ...formData.financial,
        revenue2024: formData.financial.revenue2024 ? parseInt(formData.financial.revenue2024) : null,
        employees2024: formData.financial.employees2024 ? parseInt(formData.financial.employees2024) : null
      }
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="edit-entity-modal">
      <div className="bg-[#1a1a1a] border border-gray-800 rounded-xl w-full max-w-2xl">
        <div className="p-6 border-b border-gray-800">
          <h2 className="text-xl font-bold text-white">Edit Entity</h2>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Legal Entity Name</label>
            <input
              type="text"
              value={formData.legalName}
              onChange={(e) => setFormData({ ...formData, legalName: e.target.value })}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Country</label>
              <select
                value={formData.jurisdiction.country}
                onChange={(e) => setFormData({
                  ...formData,
                  jurisdiction: { ...formData.jurisdiction, country: e.target.value }
                })}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {COUNTRIES.map(country => (
                  <option key={country.value} value={country.value}>{country.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Revenue (2024)</label>
              <input
                type="number"
                value={formData.financial.revenue2024 || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  financial: { ...formData.financial, revenue2024: e.target.value }
                })}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-800">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-400 hover:text-white">
              Cancel
            </button>
            <button type="submit" className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// View Entity Modal
function ViewEntityModal({ entity, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="view-entity-modal">
      <div className="bg-[#1a1a1a] border border-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-800 flex items-center justify-between">
          <div>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              entity.entityType === 'parent' 
                ? 'bg-purple-500/20 text-purple-300' 
                : 'bg-blue-500/20 text-blue-300'
            }`}>
              {entity.entityType}
            </span>
            <h2 className="text-xl font-bold text-white mt-2">{entity.legalName}</h2>
            {entity.tradingName && entity.tradingName !== entity.legalName && (
              <p className="text-sm text-gray-400">d/b/a {entity.tradingName}</p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            ✕
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Jurisdiction */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-800/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Globe className="h-4 w-4 text-gray-400" />
                <span className="text-xs text-gray-400 uppercase">Country</span>
              </div>
              <p className="text-white font-medium">{entity.jurisdiction?.country || '-'}</p>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="h-4 w-4 text-gray-400" />
                <span className="text-xs text-gray-400 uppercase">Currency</span>
              </div>
              <p className="text-white font-medium">{entity.financial?.reportingCurrency || 'USD'}</p>
            </div>
          </div>

          {/* Financial */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-800/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="h-4 w-4 text-gray-400" />
                <span className="text-xs text-gray-400 uppercase">Revenue (2024)</span>
              </div>
              <p className="text-white font-medium">
                {entity.financial?.revenue2024 
                  ? `${entity.financial.reportingCurrency} ${(entity.financial.revenue2024 / 1000000000).toFixed(2)}B`
                  : '-'
                }
              </p>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-gray-400" />
                <span className="text-xs text-gray-400 uppercase">Employees</span>
              </div>
              <p className="text-white font-medium">
                {entity.financial?.employees2024?.toLocaleString() || '-'}
              </p>
            </div>
          </div>

          {/* Applicable Regulations */}
          <div>
            <h3 className="text-sm font-medium text-gray-400 uppercase mb-3">Applicable Regulations</h3>
            {entity.applicableRegulations?.length > 0 ? (
              <div className="space-y-2">
                {entity.applicableRegulations.map((reg, idx) => (
                  <div key={idx} className="bg-gray-800/50 rounded-lg p-4 flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <Scale className="h-4 w-4 text-purple-400" />
                        <span className="text-white font-medium">{reg.regulationCode}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          reg.status === 'applicable' 
                            ? 'bg-green-500/20 text-green-300'
                            : 'bg-yellow-500/20 text-yellow-300'
                        }`}>
                          {reg.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-400 mt-1">{reg.regulationName}</p>
                      <p className="text-xs text-gray-500 mt-1">{reg.applicabilityReason}</p>
                    </div>
                    {reg.firstReportDue && (
                      <div className="text-right">
                        <p className="text-xs text-gray-500">First Report Due</p>
                        <p className="text-sm text-white">
                          {new Date(reg.firstReportDue).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 italic">No regulations auto-detected for this entity.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Migration Modal
function MigrationModal({ entities, onClose, onMigrate }) {
  const [selectedEntityId, setSelectedEntityId] = useState('');

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="migration-modal">
      <div className="bg-[#1a1a1a] border border-gray-800 rounded-xl w-full max-w-md">
        <div className="p-6 border-b border-gray-800">
          <h2 className="text-xl font-bold text-white">Migrate Existing Data</h2>
          <p className="text-sm text-gray-400 mt-1">
            Assign existing obligations, evidence, and claims to an entity.
          </p>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Assign existing data to:
            </label>
            <select
              value={selectedEntityId}
              onChange={(e) => setSelectedEntityId(e.target.value)}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">Select entity...</option>
              {entities.map(entity => (
                <option key={entity._id} value={entity._id}>
                  {entity.legalName} ({entity.entityType})
                </option>
              ))}
            </select>
          </div>

          <div className="bg-yellow-900/30 border border-yellow-700/50 rounded-lg p-4">
            <p className="text-sm text-yellow-200">
              <strong>Note:</strong> This will assign all existing obligations, evidence items, 
              and claims that don't have an entity assigned to the selected entity.
            </p>
          </div>
        </div>

        <div className="p-6 border-t border-gray-800 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onMigrate(selectedEntityId)}
            disabled={!selectedEntityId}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Migrate Data
          </button>
        </div>
      </div>
    </div>
  );
}
